import { AnyAction } from 'redux';
import { createAsyncThunk, createSelector, createSlice, PayloadAction, ThunkDispatch } from '@reduxjs/toolkit';

import type { MessagesForConversation, PresentationBuilderChatState, PresentationChatMessage, PresentationChatRequest } from '../types/presentationBuilderChat';
import type { Conversation } from '../types/skubot';
import { StoreState } from '../reducers/core';
import { skubotApi } from '../components/skubot/lib/skubotApi';
import { generateUUID } from '../utils';
import { createLoadOrder } from '../actions/order';
import { getErrorMessageFromAction, getMessageFromResponseError } from './utils';

const initialState: PresentationBuilderChatState = {
    messages: {},
    activeConversation: undefined,
    initialContextSent: false,
    loading: false,
    previewRevision: 0,
    errors: {},
    conversationTranscript: [],
};

const chat = createAsyncThunk<PresentationChatMessage, PresentationChatRequest>(
    'presentationBuilderChat/chat',
    async (request, thunkAPI) => {
        try {
            return await skubotApi.presentationChat(request);
        } catch (error) {
            return thunkAPI.rejectWithValue(getMessageFromResponseError(error));
        }
    }
);


const ERROR_KEYS = {
    MESSAGE_ERROR: 'MESSAGE_ERROR',
    CONVERSATION_ERROR: 'CONVERSATION_ERROR',
};

const presentationBuilderChatSlice = createSlice({
    name: 'presentationBuilderChat',
    initialState,
    reducers: {
        setMessages(state, action: PayloadAction<{ [key: string]: PresentationChatMessage }>) {
            state.messages = action.payload;
        },
        setActiveConversation(state, action: PayloadAction<Conversation | undefined>) {
            state.activeConversation = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setErrors(state, action: PayloadAction<{ [key: string]: string | null }>) {
            state.errors = action.payload;
        },
        setMessageError(state, action: PayloadAction<string | null>) {
            state.errors[ERROR_KEYS.MESSAGE_ERROR] = action.payload;
        },
        setConversationError(state, action: PayloadAction<string | null>) {
            state.errors[ERROR_KEYS.CONVERSATION_ERROR] = action.payload;
        },
        setConversationTranscript(state, action: PayloadAction<MessagesForConversation[]>) {
            state.conversationTranscript = action.payload;
        },
        addMessage(state, action: PayloadAction<PresentationChatMessage>) {
            state.messages[action.payload.id] = action.payload;
        },
        setInitialContextSent(state, action: PayloadAction<boolean>) {
            state.initialContextSent = action.payload;
        },
        incrementPreviewRevision(state) {
            state.previewRevision += 1;
        },
        setPreviewRevision(state, action: PayloadAction<number>) {
            state.previewRevision = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(chat.pending, (state) => {
                state.loading = true;
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = null;
            })
            .addCase(chat.fulfilled, (state, action) => {
                if (!state.activeConversation?.sessionId) {
                    state.activeConversation = {
                        id: action.payload.conversationId,
                        sessionId: action.payload.conversationId,
                        title: 'New Conversation',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    };
                }
                // Overwrite the message in the messages object (Thinking... feedback message)
                state.messages[action.payload.id] = action.payload;
                state.loading = false;
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = null;

                // Add the message to the conversation transcript
                switch(action.payload.status) {
                    case 'awaiting_input':
                        state.conversationTranscript = ([
                            ...state.conversationTranscript,
                            {
                                role: 'user',
                                content: action.meta.arg.user_query,
                            },
                            {
                                role: 'assistant',
                                content: action.payload.content,
                            },
                        ]);
                        break;
                    default:
                        state.conversationTranscript = [];
                        break;
                }
            })
            .addCase(chat.rejected, (state, action) => {
                state.errors[ERROR_KEYS.MESSAGE_ERROR] = getErrorMessageFromAction(action, "skubot's response could not be fully generated.");
                delete state.messages[action.meta.arg.message_id];
                state.loading = false;
            });
    },
});

export {
    chat,
};

export const {
    setMessages,
    setActiveConversation,
    setLoading,
    setErrors,
    setMessageError,
    setConversationError,
    setConversationTranscript,
    addMessage,
    setInitialContextSent,
    incrementPreviewRevision,
    setPreviewRevision,
} = presentationBuilderChatSlice.actions;

export const createNewConversation = (message?: string) => async (dispatch: ThunkDispatch<StoreState, undefined, AnyAction>, getState: () => StoreState) => {
    let sessionId: string;
    try {
        sessionId = await skubotApi.generateSessionId();
    } catch (error) {
        sessionId = generateUUID();
    }

    const conversation: Conversation = {
        id: sessionId,
        sessionId: sessionId,
        title: message ? message.substring(0, 200) + (message.length > 200 ? '...' : '') : 'New Conversation',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };

    dispatch(setActiveConversation(conversation));
    return conversation;
};

export const sendChatMessage = (
    message: string,
    clientName: string,
    tenantId: string,
    orderId: string,
    eventType: string,
    totalBudget?: number,
    currentProducts: string[] = []
) => async (dispatch: ThunkDispatch<StoreState, undefined, AnyAction>, getState: () => StoreState) => {
    dispatch(setMessageError(null));
    dispatch(setConversationError(null));

    const initialContextSent = getState().presentationBuilderChat.initialContextSent;
    if (!initialContextSent) {
        if (!clientName || !tenantId || !orderId) {
            dispatch(setConversationError('Additional context is required to start a conversation.'));
            return;
        }
        dispatch(setInitialContextSent(true));
    }

    let activeConversation = selectActiveConversation(getState());
    if (!activeConversation) {
        const conversation = await dispatch(createNewConversation(message));
        activeConversation = conversation;
    }
    const assistantMessageId = generateUUID();
    const userMessageId = generateUUID();

    dispatch(addMessage({
        id: userMessageId,
        conversationId: activeConversation.id,
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
    }));
    
    // Add initial message from the assistant - Thinking... feedback message
    dispatch(addMessage({
        id: assistantMessageId,
        conversationId: activeConversation.id,
        role: 'assistant',
        content: 'Thinking...',
        createdAt: new Date().toISOString(),
    }));

    // Send message to the assistant
    const result = await dispatch(chat({
        session_id: activeConversation.sessionId,
        message_id: assistantMessageId,
        user_query: message,
        client_name: clientName,
        tenant_id: tenantId,
        order_id: orderId,
        event_type: eventType,
        total_budget: totalBudget,
        current_products: currentProducts,
        messages: getState().presentationBuilderChat.conversationTranscript,
    }));

    // If the assistant has added products to the presentation, load the order
    if (chat.fulfilled.match(result) && (result.payload.numProductsAdded ?? 0) > 0) {
        dispatch(incrementPreviewRevision());
        dispatch(createLoadOrder(orderId, true));
    }
};

export const selectMessages = (state: StoreState) => state.presentationBuilderChat.messages;
export const selectActiveConversation = (state: StoreState) => state.presentationBuilderChat.activeConversation;
export const selectLoading = (state: StoreState) => state.presentationBuilderChat.loading;
export const selectErrors = (state: StoreState) => state.presentationBuilderChat.errors;
export const selectMessageError = (state: StoreState) => state.presentationBuilderChat.errors[ERROR_KEYS.MESSAGE_ERROR] ?? null;
export const selectConversationError = (state: StoreState) => state.presentationBuilderChat.errors[ERROR_KEYS.CONVERSATION_ERROR] ?? null;
export const selectInitialContextSent = (state: StoreState) => state.presentationBuilderChat.initialContextSent;
export const selectPreviewRevision = (state: StoreState) => state.presentationBuilderChat.previewRevision;

export const selectMessagesForConversation = createSelector(
    selectMessages,
    selectActiveConversation,
    (messages, activeConversation) => (
        activeConversation
            ? Object.values(messages).filter(message => message.conversationId === activeConversation.id)
            : []
    ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
);

export default presentationBuilderChatSlice.reducer;
